import React from "react";
import Konva from 'konva'; // Dodano import Konva
import { Layer, Rect, Transformer, Line, Text } from "react-konva";
import EditableText from "../EditableText";
import LoadableImage from "../LoadableImage";
import { parseGuideExpression, posToMm } from "./GuideUtils"; // Importujemy potrzebne funkcje

// Komponent renderujący zawartość warstwy Konva
export default function EditorCanvasRenderer({
  layerRef, // Ref dla warstwy
  transformerRef, // Ref dla transformera
  pages,
  currentPage,
  validPage,
  baseWidth,
  baseHeight,
  zoomFactor,
  selectedShapeId,
  setSelectedShapeId,
  editingTextId,
  setEditingTextId,
  handleChangeText,
  handleChangeImage,
  handleChangeSvg,
  publicMode,
  guides, // Stałe prowadnice
  tempGuides, // Tymczasowe prowadnice (snap)
  handleObjectDragMove, // Handler przeciągania
  handleDragEnd, // Handler końca przeciągania
  fontsAreLoaded, // Czy czcionki są załadowane
  stageRef, // Ref do stage'a (potrzebny w EditableText)
  isDragging, // Odbieramy nowy prop
  // Odbieramy propsy ze zmienionymi nazwami
  pageIsPersonalized,
  pageGuides,
  pageBaseWidth,
  pageBaseHeight,
  backendUrl, // Dodajemy backendUrl do propsów
  foldLineConfig // Nowy prop dla konfiguracji linii zgięcia
}) {

  // Funkcja renderująca obiekty Konva (teksty, obrazy, svg)
  function renderKonvaObjects() {
    if (!validPage) return null;
    const pg = pages[currentPage];
    const allTexts = pg.texts || [];
    const allImages = pg.images || [];
    const allSvgs = pg.svgs || [];

    const items = [
      ...allTexts.map((t) => ({ ...t, _type: "text" })),
      ...allImages.map((m) => ({ ...m, _type: "image" })),
      ...allSvgs.map((s) => ({ ...s, _type: "svg" }))
    ];

    // Sortowanie obiektów do renderowania (SVG tła na spodzie, potem obrazy, potem teksty)
    const getPriority = (obj) => {
      if (obj._type === "svg") {
        const area = (obj.width || 0) * (obj.height || 0);
        const pageArea = baseWidth * baseHeight;
        // Duże SVG (tło) mają priorytet 0
        return area > 0.8 * pageArea ? 0 : 2;
      } else if (obj._type === "image") return 1;
      else if (obj._type === "text") return 3;
      return 99; // Domyślny priorytet
    };
    items.sort((a, b) => {
      const pa = getPriority(a);
      const pb = getPriority(b);
      if (pa !== pb) return pa - pb;
      // Dodatkowe sortowanie wg subZ dla tego samego priorytetu
      return (a.subZ || 0) - (b.subZ || 0);
    });

    return items.map((obj) => {
      const locked = obj.flags?.locked || false;
      const finalDraggable = !(publicMode && locked);
      const finalListening = !(publicMode && locked); // Obiekty zablokowane nie reagują na zdarzenia

      if (obj._type === "text") {
        return (
          <EditableText
            key={obj.id}
            textObj={obj}
            isSelected={selectedShapeId === obj.id}
            onSelect={setSelectedShapeId}
            onChange={handleChangeText}
            stageRef={stageRef} // Przekazujemy stageRef
            editingTextId={editingTextId}
            setEditingTextId={setEditingTextId}
            draggable={finalDraggable}
            listening={finalListening}
            onDragMove={handleObjectDragMove}
            onDragEnd={handleDragEnd}
            id={obj.id}
            fontsAreLoaded={fontsAreLoaded}
            onTransformEnd={(ev) => { // Handler transformacji dla tekstu
              const node = ev.target;
              handleChangeText({
                id: node.id(),
                x: node.x(),
                y: node.y(),
                scaleX: node.scaleX(),
                scaleY: node.scaleY(),
                rotation: node.rotation()
              });
            }}
            // Przekazujemy dodatkowe propsy do EditableText pod oryginalnymi nazwami
            isPersonalizedProject={pageIsPersonalized}
            guides={pageGuides}
            baseWidth={pageBaseWidth}
            baseHeight={pageBaseHeight}
          />
        );
      } else if (obj._type === "image" || obj._type === "svg") {
        const changeHandler = obj._type === "image" ? handleChangeImage : handleChangeSvg;
        // Wspólny handler transformacji dla obrazów i SVG
        const handleTransformEndGeneric = (e) => {
            const node = e.target;
            const scaleX = node.scaleX();
            const scaleY = node.scaleY();
            const rotation = node.rotation();
            const x = node.x();
            const y = node.y();

            if (obj._type === 'image') {
                 // Dla obrazów resetujemy skalę i zapisujemy nowe wymiary
                 const newW = (obj.width || 100) * scaleX;
                 const newH = (obj.height || 100) * scaleY;
                 node.setAttrs({ width: newW, height: newH, scaleX: 1, scaleY: 1 });
                 changeHandler({ id: node.id(), x, y, rotation, width: newW, height: newH });
            } else { // dla SVG zapisujemy skalę
                 node.setAttrs({ scaleX: 1, scaleY: 1 }); // Resetujemy skalę węzła Konva
                 // Zapisujemy skalę w stanie (przekazujemy do changeHandler)
                 changeHandler({ id: node.id(), x, y, rotation, scaleX: scaleX, scaleY: scaleY });
            }
        };

        // Tworzymy pełny URL dla obrazka/SVG, używając bezpiecznej trasy API
        const finalShape = { ...obj };
        if (finalShape.src && finalShape.src.startsWith('/uploads/')) {
          // Usuwamy wiodący '/uploads/' i dodajemy prefix bezpiecznej trasy
          const relativePath = finalShape.src.substring('/uploads/'.length);
          finalShape.src = `${backendUrl}/admin/uploads-secure/${relativePath}`;
          // console.log(`[Renderer] Constructed secure URL: ${finalShape.src}`); // Opcjonalny log
        } else if (finalShape.src && (
          finalShape.src.startsWith('/client/qr-file/') || 
          finalShape.src.startsWith('/client/blissaro/download-qr/') ||
          finalShape.src.startsWith('/api/client/qr-file/') ||
          finalShape.src.startsWith('/api/client/blissaro/download-qr/') ||
          finalShape.src.includes('kreator.amelia-wedding.pl/client/') ||
          finalShape.src.includes('kreator.amelia-wedding.pl/api/client/')
        )) {
          // Napraw względne ścieżki QR kodów lub popraw HTTP na HTTPS i dodaj /api prefix
          if (finalShape.src.startsWith('/client/')) {
            // Stara względna ścieżka bez /api - dodaj /api prefix
            finalShape.src = `${backendUrl}/api${finalShape.src}`;
          } else if (finalShape.src.startsWith('/api/client/')) {
            // Nowa względna ścieżka z /api - dodaj tylko backendUrl
            finalShape.src = `${backendUrl}${finalShape.src}`;
          } else if (finalShape.src.includes('kreator.amelia-wedding.pl/client/') && !finalShape.src.includes('/api/')) {
            // Bezwzględny URL bez /api prefix - dodaj /api
            finalShape.src = finalShape.src.replace('/client/', '/api/client/');
          }
          
          // Napraw protokół HTTP na HTTPS
          if (finalShape.src.startsWith('http://kreator.amelia-wedding.pl/')) {
            finalShape.src = finalShape.src.replace('http://kreator.amelia-wedding.pl/', 'https://kreator.amelia-wedding.pl/');
          }
          
          console.log(`[Renderer] Fixed QR URL: ${finalShape.src}`); // Log dla debugowania
        }


        return (
          <LoadableImage // Używamy LoadableImage dla obu typów
            key={finalShape.id}
            shape={finalShape} // Przekazujemy zmodyfikowany obiekt shape z pełnym URL
            isSelected={selectedShapeId === finalShape.id}
            onSelect={setSelectedShapeId}
            onChange={changeHandler} // Używamy odpowiedniego handlera (handleChangeImage lub handleChangeSvg)
            publicMode={publicMode}
            draggable={finalDraggable}
            listening={finalListening}
            onDragMove={handleObjectDragMove}
            onDragEnd={handleDragEnd}
            id={finalShape.id}
            onTransformEnd={handleTransformEndGeneric} // Używamy wspólnego handlera transformacji
          />
        );
      }
      return null; // Zwracamy null dla nieobsługiwanych typów
    });
  }

  // Funkcja renderująca stałe prowadnice
  function renderGuides() {
    // Renderuj tylko podczas przeciągania
    if (!isDragging || !validPage || !guides || guides.length === 0) return null;

    // console.log("[GUIDE RENDER] renderGuides called (isDragging=true)"); // Removed log

    return guides.flatMap((gd) => {
      if (!gd.id || !gd.expression) return [];
      try {
        const strokeWidth = 1 / zoomFactor;
        const dash = [4 / zoomFactor, 4 / zoomFactor];
        const fontSize = Math.max(8, 12 / zoomFactor);
        const textOffset = 5 / zoomFactor;

        if (gd.orientation === "H") {
          const pos = parseGuideExpression(gd.expression, baseHeight);
          if (isNaN(pos)) throw new Error("Invalid position calculated");
          const mmVal = posToMm(pos);
          return [
            <Line
              key={`${gd.id}-line`}
              points={[0, pos, baseWidth, pos]}
              stroke="#888888"
              strokeWidth={strokeWidth}
              dash={dash}
              listening={false}
              perfectDrawEnabled={false}
            />,
            <Text
              key={`${gd.id}-text`}
              text={`${mmVal.toFixed(1)}mm`}
              x={textOffset}
              y={pos + textOffset}
              fontSize={fontSize}
              fill="#888888"
              listening={false}
              perfectDrawEnabled={false}
            />
          ];
        } else if (gd.orientation === "V") {
          const pos = parseGuideExpression(gd.expression, baseWidth);
          if (isNaN(pos)) throw new Error("Invalid position calculated");
          const mmVal = posToMm(pos);
          return [
            <Line
              key={`${gd.id}-line`}
              points={[pos, 0, pos, baseHeight]}
              stroke="#888888"
              strokeWidth={strokeWidth}
              dash={dash}
              listening={false}
              perfectDrawEnabled={false}
            />,
            <Text
              key={`${gd.id}-text`}
              text={`${mmVal.toFixed(1)}mm`}
              x={pos + textOffset}
              y={textOffset}
              fontSize={fontSize}
              fill="#888888"
              listening={false}
              perfectDrawEnabled={false}
            />
          ];
        } else {
          return []; // Ignorujemy nieprawidłowe orientacje
        }
      } catch (e) {
        console.error(`Error parsing or rendering guide expression "${gd.expression}":`, e);
        return []; // Zwracamy pustą tablicę w przypadku błędu
      }
    });
  }

  // Funkcja renderująca tymczasowe prowadnice (snap)
  function renderTempPublicGuides() {
    // Renderuj tylko podczas przeciągania i gdy są prowadnice
    if (!isDragging || !tempGuides || tempGuides.length === 0) {
        // Log even when not rendering if dragging is false but guides exist
        // if (!isDragging && tempGuides && tempGuides.length > 0) { // Removed log
        //      console.log("[GUIDE RENDER] renderTempPublicGuides NOT rendering because isDragging=false, but tempGuides exist:", tempGuides); // Removed log
        // } // Removed log
        return null;
    }

    // console.log("[GUIDE RENDER] renderTempPublicGuides called (isDragging=true). tempGuides:", tempGuides); // Removed log
    const strokeWidth = 1 / zoomFactor;
    const dash = [4 / zoomFactor, 2 / zoomFactor]; // Styl linii przerywanej
    const strokeColor = "#888888"; // Kolor linii

    return tempGuides.map((guide, idx) => {
        // Podstawowa walidacja struktury prowadnicy
        if (!guide || !Array.isArray(guide.points) || !guide.orientation) {
            // console.warn("Invalid temp guide structure:", guide); // Removed log
            return null; // Ignorujemy nieprawidłowe prowadnice
        }
        return (
            <Line
                key={`tempGuide-${idx}`}
                points={guide.points}
                stroke={strokeColor}
                strokeWidth={strokeWidth}
                dash={dash}
                listening={false} // Linie nie reagują na zdarzenia
                perfectDrawEnabled={false} // Optymalizacja wydajności
            />
        );
    });
  }

  // Funkcja renderująca linię zgięcia
  function renderFoldLine() {
    console.log('[Renderer] renderFoldLine called. foldLineConfig:', foldLineConfig, 'validPage:', validPage);
    if (!validPage || !foldLineConfig || !foldLineConfig.type) {
      console.log('[Renderer] renderFoldLine: Bailing out due to invalid page or foldLineConfig.');
      return null;
    }

    const { type, color = '#FF0000', text: labelText = 'linia zgięcia' } = foldLineConfig;
    console.log('[Renderer] renderFoldLine: Rendering with type:', type, 'color:', color, 'text:', labelText);
    
    const strokeWidth = 1 / zoomFactor;
    const dash = [10 / zoomFactor, 5 / zoomFactor];
    const fontSize = Math.max(10, 12 / zoomFactor); // Zmniejszono trochę fontSize dla lepszego dopasowania
    const textPadding = 8 / zoomFactor; // Zwiększono padding dla lepszej czytelności przerwy
    // const lineOffset = textPadding / 2; // Usunięto nieużywaną zmienną

    // Utwórz tymczasowy obiekt Text, aby zmierzyć jego wymiary (bez wewnętrznego paddingu)
    const tempText = new Konva.Text({
        text: labelText,
        fontSize: fontSize,
        // padding: textPadding / 2, // Usunięto padding z pomiaru, aby uzyskać czyste wymiary tekstu
    });
    const textMeasuredWidth = tempText.width(); // Czysta szerokość tekstu
    const textMeasuredHeight = tempText.height(); // Czysta wysokość tekstu

    const elements = [];

    if (type === 'horizontal') {
      const yPos = baseHeight / 2; // Oś linii zgięcia
      const gapWidth = textMeasuredWidth + textPadding; // Całkowita szerokość przerwy na tekst i padding

      // Linia 1 (przed tekstem)
      elements.push(
        <Line
          key="fold-line-h1"
          points={[0, yPos, (baseWidth - gapWidth) / 2, yPos]}
          stroke={color}
          strokeWidth={strokeWidth}
          dash={dash}
          listening={false}
          perfectDrawEnabled={false}
        />
      );
      // Linia 2 (za tekstem)
      elements.push(
        <Line
          key="fold-line-h2"
          points={[(baseWidth + gapWidth) / 2, yPos, baseWidth, yPos]}
          stroke={color}
          strokeWidth={strokeWidth}
          dash={dash}
          listening={false}
          perfectDrawEnabled={false}
        />
      );
      // Tekst
      elements.push(
        <Text
          key="fold-line-text-h"
          text={labelText}
          x={baseWidth / 2}
          y={yPos} // Ustawiamy y na osi linii
          fontSize={fontSize}
          fill={color}
          width={textMeasuredWidth}
          height={textMeasuredHeight}
          align="center"
          verticalAlign="middle" // Wyśrodkowanie pionowe tekstu względem jego y
          offsetX={textMeasuredWidth / 2}
          offsetY={textMeasuredHeight / 2} // Centrujemy tekst na jego środku pionowym
          listening={false}
          perfectDrawEnabled={false}
        />
      );
    } else if (type === 'vertical') {
      const xPos = baseWidth / 2; // Oś linii zgięcia
      // Dla linii pionowej, "wysokość" przerwy zależy od szerokości tekstu (po obrocie)
      const gapHeight = textMeasuredWidth + textPadding; 

      // Linia 1 (nad tekstem)
      elements.push(
        <Line
          key="fold-line-v1"
          points={[xPos, 0, xPos, (baseHeight - gapHeight) / 2]}
          stroke={color}
          strokeWidth={strokeWidth}
          dash={dash}
          listening={false}
          perfectDrawEnabled={false}
        />
      );
      // Linia 2 (pod tekstem)
      elements.push(
        <Line
          key="fold-line-v2"
          points={[xPos, (baseHeight + gapHeight) / 2, xPos, baseHeight]}
          stroke={color}
          strokeWidth={strokeWidth}
          dash={dash}
          listening={false}
          perfectDrawEnabled={false}
        />
      );
      // Tekst
      elements.push(
        <Text
          key="fold-line-text-v"
          text={labelText}
          x={xPos} // Ustawiamy x na osi linii
          y={baseHeight / 2} // Ustawiamy y na środku płótna
          fontSize={fontSize}
          fill={color}
          width={textMeasuredWidth}  // Szerokość tekstu przed obrotem
          height={textMeasuredHeight} // Wysokość tekstu przed obrotem
          align="center"
          verticalAlign="middle"   // Wyśrodkowanie "pionowe" tekstu (względem jego oryginalnej osi Y)
          rotation={-90}
          offsetX={textMeasuredWidth / 2} // Centrujemy na środku oryginalnej szerokości
          offsetY={textMeasuredHeight / 2}   // Centrujemy na środku oryginalnej wysokości
          listening={false}
          perfectDrawEnabled={false}
        />
      );
    } else {
      console.log('[Renderer] renderFoldLine: Unknown fold line type:', type);
      return null;
    }

    console.log('[Renderer] renderFoldLine: Returning Line and Text elements.');
    return <React.Fragment>{elements}</React.Fragment>;
  }

  // Renderowanie warstwy Konva
  return (
    <Layer ref={layerRef} imageSmoothingEnabled={false}>
      {/* Tło strony (biały prostokąt) */}
      <Rect
        x={0}
        y={0}
        width={baseWidth}
        height={baseHeight}
        fill="#fff"
        listening={false} // Tło nie reaguje na zdarzenia
        perfectDrawEnabled={false} // Optymalizacja
      />
      {/* Renderowanie obiektów (teksty, obrazy, svg) */}
      {renderKonvaObjects()}
      {/* Renderowanie linii zgięcia */}
      {renderFoldLine()}
      {/* Renderowanie stałych prowadnic */}
      {renderGuides()}
      {/* Renderowanie tymczasowych prowadnic (snap) */}
      {renderTempPublicGuides()}

      {/* Transformer do zmiany rozmiaru/obrotu zaznaczonych obiektów */}
      <Transformer
        ref={transformerRef}
        borderStroke="#cccccc" // Jasnoszara ramka
        borderDash={[4, 4]} // Linia przerywana
        anchorSize={8} // Rozmiar uchwytów
        anchorCornerRadius={4} // Zaokrąglenie uchwytów (koła)
        anchorStroke="#999999" // Kolor obramowania uchwytów
        anchorFill="#eeeeee" // Kolor wypełnienia uchwytów
        padding={0} // Odstęp od obiektu
        // keepRatio i rotateEnabled są zarządzane dynamicznie w useEditorCanvasLogic
      />
    </Layer>
  );
}
